import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  find as _find,
  get as _get,
  has as _has,
  includes as _includes,
  some as _some
} from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import {
  BillAccountActions,
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import { env } from '@amfam/environment';
import { PolicySelectors, PolicySummarySelectors } from '@amfam/policy/data-access';
// Store
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillingAnalytics,
  BillingElementIds,
  BillingPaymentPaths,
  FuturePaymentList,
  INVALID_IMPERSONATION_STATUS_CODE,
  ONLINE_BILLING,
  PcmAnalytics,
  PolicyList
} from '@amfam/shared/models';
import { QuickLink, QuickLinksService } from '@amfam/shared/ui/quick-links';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  Action,
  DockingBarService,
  DsModalService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';
import { PrettyBillingAcctNum } from '../../shared/ui/format-pipes/format.pipe';

@Component({
  selector: 'app-bill-account-detail',
  templateUrl: './bill-account-detail.component.html',
  styleUrls: ['../shared/bill-accounts.scss'],
  standalone: false
})
export class BillAccountDetailComponent implements OnInit, OnDestroy {
  public contactNumber: string;
  public hasAutopayEnabled = false;
  public showPaymentAddComp = false;
  public isBam: boolean;
  public hasClassicPolicies$: Observable<boolean>;
  public futurePayments$: Observable<FuturePaymentList>;

  readOnly = false;
  onlineBilling = ONLINE_BILLING;
  columns: Array<{}> = [];
  public matchedBillAccountNotificationState: BillingNotificationsState;

  public unAssociatedAccount: PolicyList;
  public autoPayAccordionAnalytic = BillingAnalytics.clickRegisteredDetailsAutoPayAccordion;
  public scheduledPaymentAccordionAnalytic =
    BillingAnalytics.clickRegisteredDetailsScheduledPaymentsAccordion;
  public silentRegistrationErrorCard = false;
  public silentRegistrationErrorScheduled = false;
  public isEligibleBillAccountForAutoPay = false;
  public hideMakePaymentLink = false;

  public silentRegistrationErrorAutomatic = false;
  public errorList: Array<ErrorObj> = [];
  public checkOpenFuturePayment$: Observable<boolean>;

  public accordionStatus = {
    paymentsAccordion: false,
    scheduleAccordion: false,
    preferencesAccordion: false,
    paymentHistoryAccordion: false,
    billingHistoryAccordion: false,
    unregisterAccordion: false,
    stopManaging: false
  };

  public billAccount: BillAccount;
  public quickLinksOverride = new Array<QuickLink>();
  public hasAdvancedPolicies: Observable<boolean>;
  // Flags to determine new vs old billing design
  public newBillingDesign = this.featureFlagService.isEnabled('new-billing-design');
  public oldBillingDesign = this.featureFlagService.isEnabled('old-billing-design');

  paymentHistoryElementIdConst = BillingElementIds.PAYMENT_HISTORY_ACCORDION;
  tryAgainModal = 'stop-managing-try-again-modal';
  tryAgainCounter = 5;
  deleteInProgress = false;
  isContactBillingToggleEnabled = this.featureFlagService.isEnabled('contact_billing');

  manageAutoPay = this.copyService.getCopy('billing.billingDetailSections.manageAutoPay');
  myPaymentMethods = this.copyService.getCopy('billing.billingDetailSections.myPaymentMethods');
  preferences = this.copyService.getCopy('billing.billingDetailSections.preferences');
  paymentHistory = this.copyService.getCopy('billing.billingDetailSections.paymentHistory');
  statementHistory = this.copyService.getCopy('billing.billingDetailSections.statementHistory');
  stopManagingAccount = this.copyService.getCopy(
    'billing.billingDetailSections.stopManagingAccount'
  );
  futurePaymentsSchedule = this.copyService.getCopy(
    'billing.billingDetailSections.futurePaymentsSchedule'
  );
  manageOneTimePayments = this.copyService.getCopy(
    'billing.billingDetailSections.manageOneTimePayments'
  );

  cssClasses = 'material noshadow ';
  private isConnectPartner = false;
  private cusCareNumber: string;
  private billAccountNumber: string;
  private emailAddress: string;

  // **** Start of Analytics data for this component
  private pageBaRegisteredAnalytic = BillingAnalytics.pageBaRegisteredAnalytic;
  private expandAllClickAnalytic = BillingAnalytics.clickRegisteredDetailsAccordionExpandAll;

  // **** End of Analytics data for this component

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private dockingService: DockingBarService,
    private copyService: CopyService,
    private pageTitle: PageTitleService,
    private store: Store,
    private route: ActivatedRoute,
    public spinner: LoadingSpinnerService,
    private analyticsFacade: AnalyticsFacade,
    private featureFlagService: FeatureFlagService,
    private quickLinksService: QuickLinksService,
    private modalService: DsModalService
  ) {
    this.store
      .select(userQuery.selectUserState)
      .pipe(takeUntil(this.stop$))
      .subscribe(currentUser => {
        if (currentUser) {
          this.emailAddress = currentUser.emailAddress;
        }
      });
  }

  ngOnInit() {
    this.spinner.start();
    this.route.params
      .pipe(
        takeUntil(this.stop$),
        withLatestFrom(
          this.store.select(BillAccountsSelectors.selectBillAccountNotificationsEntities)
        )
      )
      .subscribe(([params, notificationsEntities]) => {
        this.billAccountNumber = params['id'];
        this.matchedBillAccountNotificationState = notificationsEntities[this.billAccountNumber];
        this.dockingService.registerHeading('');
        if (this.isContactBillingToggleEnabled) {
          this.dockingService.registerAction(
            new Action('Contact Billing', () => {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.BILLING_CONTACT, this.billAccountNumber]
                })
              );
            })
          );
        }
      });

    this.store
      .select(BrandSelectors.selectIsPartner)
      .pipe(take(1))
      .subscribe(isPartner => {
        // Check if user is non-AFI partner
        this.isConnectPartner = isPartner;
      });

    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(PolicySummarySelectors.selectPolicySummaries),
      this.store.select(PolicySelectors.selectPolicies),
      this.store.select(BillAccountsSelectors.selectBillAccountsLoading),
      this.store.select(AutoPaySelectors.getAnyLoading),
      this.store.select(fromRoot.getBillaccountsListEligibleForAutopay),
      this.store.select(AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay),
      this.route.params,
      this.store.select(BillAccountsSelectors.selectAllBillAccountsNotifications),
      (
        billAccounts,
        automaticPayments,
        policySummaries,
        policies,
        billAccountsLoading,
        automaticPaymentsLoading,
        oldBillAccountsEligibleForAutoPay,
        billAccountsEligibleForAutoPay,
        routeParams,
        billAccountsNotifications
      ) => ({
        billAccounts: billAccounts,
        automaticPayments: automaticPayments,
        policySummaries: policySummaries,
        policies: policies,
        loadingOrRegistering:
          billAccountsLoading ||
          automaticPaymentsLoading ||
          _some(billAccountsNotifications, 'updatingRegistration'),
        eligibleBillAccts: billAccountsEligibleForAutoPay,
        routeParams: [routeParams]
      })
    )
      .pipe(
        filter(state => !state.loadingOrRegistering && _get(state, 'billAccounts.length')),
        withLatestFrom(
          this.store.select(
            BillAccountsSelectors.selectMatchedBillAccountNotification,
            this.billAccountNumber
          )
        ),
        filter(
          ([state]) =>
            !!state.billAccounts.find(
              billAccount => billAccount && billAccount.billAccountNumber === this.billAccountNumber
            )
        ),
        takeUntil(this.stop$)
      )
      .subscribe(([state]) => {
        // Set the bill account.
        this.billAccount = state.billAccounts.find(
          billAccount => billAccount && billAccount.billAccountNumber === this.billAccountNumber
        );
        // Make sure we have a bill account object. We would not have a bill account in the case
        // Where an unassociated bill account was just unregistered. Early out if we don't have one.
        if (!this.billAccount) {
          return;
        }
        // Handle partial failures
        if (
          _get(this.matchedBillAccountNotificationState, 'updateRegistrationPartialFailure', false)
        ) {
          this.modalService.open(this.tryAgainModal);
          this.analyticsFacade.trackPage(PcmAnalytics.unBAMPartialError);
        }
        // Enable retry button when delete attempt is complete
        if (_get(this.matchedBillAccountNotificationState, 'deletingPreferences', false)) {
          this.deleteInProgress = false;
        }

        // emailAddress for confirmation page.
        // TODO: AS: Need to research this along the line of --> http://www.2ality.com/2013/08/protecting-objects.html
        this.billAccount = Object.assign({}, this.billAccount, {
          emailAddress: this.emailAddress
        });

        this.updateDockBar();

        /**
         * AS: For registering/unregistering an unassociated account we do not have the risk description hence we are
         * showing the policy description to the user.
         */
        this.unAssociatedAccount = this.billAccount.policyList;
        // Set the hasAutopayEnabled flag based on matching rules
        const matchingAutopayRule = state.automaticPayments.find(
          rule => rule.billAccountNumber === this.billAccountNumber
        );
        this.hasAutopayEnabled = matchingAutopayRule ? true : false;

        this.isEligibleBillAccountForAutoPay = state.eligibleBillAccts.some(
          eligibleAccount => eligibleAccount.billAccountNumber === this.billAccountNumber
        );
        const isRegisteredHere =
          !this.billAccount.registeredElsewhere &&
          _get(this.billAccount, 'billingMethod') === ONLINE_BILLING;
        const unregisteredBam =
          _get(this.billAccount, 'billingPreferences.preferences[0].isModifiable') &&
          !!_get(this.billAccount, 'billingPreferences.preferences[0].preferenceId');
        // This is the logic API team said to use to determine bill account manager
        this.isBam = isRegisteredHere || unregisteredBam;

        // TODO:Remove readOnly member variable and use readOnly from billAccount.
        // Determine bill account eligibility.
        this.readOnly = this.billAccount.readOnly;
        this.spinner.stop();

        // If we're classic past due, send proper analytics
        if (
          this.billAccount.pastDue &&
          !this.billAccount.isAdvance &&
          this.featureFlagService.isEnabled('classic_past_due')
        ) {
          this.analyticsFacade.trackPage(BillingAnalytics.pageBillingDetailPastDue);
        }
      });

    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(fromRoot.isAnyAdvancedPoliciyInGivenPolicies, {
        dataPolicies: _get(this.billAccount, 'policyList', [])
      }),
      (billAccounts, billAccountsLoading, hasAdvancedPolicies) => ({
        billAccounts,
        billAccountsLoading,
        hasAdvancedPolicies
      })
    )
      .pipe(
        filter(state => !state.billAccountsLoading && !!state.hasAdvancedPolicies),
        map(state => {
          const matchedBillAccount = _find(state.billAccounts, [
            'billAccountNumber',
            this.billAccountNumber
          ]);
          this.futurePayments$ = of(_get(matchedBillAccount, 'futurePayments', []));
          return matchedBillAccount;
        }),
        filter(matchedBillAccount => !_has(matchedBillAccount, 'futurePayments')),
        takeUntil(this.stop$)
      )
      .subscribe(() => {
        this.setupFuturePaymentSchedule();
      });

    // Set the contact number.
    this.contactNumber = env.numberLink.split(':')[1];
    this.store
      .select(BrandSelectors.selectCusCareNumber)
      .pipe(take(1))
      .subscribe(number => (this.cusCareNumber = number));

    // Determine if the user has any advanced policies
    this.hasAdvancedPolicies = this.store.select(PolicySelectors.selectHasAdvancePolicies);

    this.pageTitle.set('Billing & Payments');

    // Determine if we need to hide the make a payment quicklink
    if (
      this.billAccount?.registeredElsewhere ||
      this.billAccount?.isExternalBam ||
      Number(this.billAccount.accountBalance) <= 0
    ) {
      this.hideMakePaymentLink = true;
    }

    this.quickLinksOverride = this.quickLinksService.buildQuickLinks(this.hideMakePaymentLink);

    if (_get(this.billAccount, 'billingMethod') === ONLINE_BILLING) {
      this.analyticsFacade.trackPage(this.pageBaRegisteredAnalytic);
    }

    // Jump to future payment accordion if coming from confirmation page
    this.checkOpenFuturePayment$ = this.route.queryParams.pipe(
      take(1),
      map(params => _get(params, 'openAccordion') === 'scheduleAccordion')
    );

    // If coming from billing email deep link, statement history accordion will open
    this.store
      .select(routerQuery.getRouterState)
      .pipe(take(1))
      .subscribe(routerState => {
        if (routerState && routerState.state) {
          if (_includes(routerState.state.url, 'billing/statementhistory')) {
            this.accordionStatus.billingHistoryAccordion = true;
          }
        }
      });

    // open manage OTP accordion if coming from Manage OTP link
    this.route.queryParams.pipe(takeUntil(this.stop$)).subscribe(queryParams => {
      if (queryParams.openAccordion === 'manageOTP') {
        this.accordionStatus.paymentsAccordion = true;
      }
    });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  toggleAccordion(accordionName: string, event: any) {
    this.accordionStatus[accordionName] = event.isOpen;
  }

  goToPaymentHistory() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/billing'],
        query: { goTo: 'paymentHistory' }
      })
    );
  }

  goToStatementHistory() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/billing'],
        query: { goTo: 'statementHistory' }
      })
    );
  }

  resetTryAgainCounter() {
    this.tryAgainCounter = 5;
  }

  expandAllClicked() {
    this.analyticsFacade.trackButtonClick(this.expandAllClickAnalytic);
  }

  silentRegistrationFailure(location: string) {
    switch (location) {
      case 'card':
        this.silentRegistrationErrorCard = true;
        break;
      case 'scheduled':
        this.silentRegistrationErrorScheduled = true;
        break;
      case 'automatic':
        this.silentRegistrationErrorAutomatic = true;
        break;
      default:
        console.error('Invalid location for silent registration error');
        break;
    }

    let message = '';
    if (
      this.matchedBillAccountNotificationState.updateRegistrationError ===
      INVALID_IMPERSONATION_STATUS_CODE
    ) {
      message = this.copyService.getCopy('billing.errorMsgsModified[0].202304');
    } else {
      message = this.copyService.getCopy('billing.cannotSchedule') + this.cusCareNumber + '.';
    }
    this.errorList = this.showErrorMessage([message]);
  }

  stopManagingTryAgain() {
    this.deleteInProgress = true;
    this.store.dispatch(
      BillAccountActions.billAccountDeletePreferenceRetry({
        billAccountNumber: this.billAccountNumber
      })
    );
    this.tryAgainCounter--;
  }

  // Check if future payment schedule is enabled and then load schedule
  private setupFuturePaymentSchedule() {
    if (!this.isConnectPartner) {
      this.store.dispatch(
        BillAccountActions.billAccountLoadFuturePayments({
          billAccountNumber: this.billAccountNumber
        } as BillAccount)
      );
    }
  }

  private updateDockBar() {
    if (!this.billAccount) {
      return;
    }

    this.dockingService.registerHeading(
      'Billing & Payments: ' +
        _get(
          this.billAccount,
          'billingPreferences.accountNickName',
          new PrettyBillingAcctNum().transform(this.billAccountNumber)
        )
    );
  }

  private showErrorMessage(
    errorMessageList: Array<string>,
    errorLevel?: ErrorLevels
  ): Array<ErrorObj> {
    let errorList: Array<ErrorObj> = [];
    errorMessageList.forEach((msg: string) => {
      if (!errorList.find(error => error.errorMessage === msg)) {
        errorList = [
          ...errorList,
          { errorMessage: msg, errorLevel: errorLevel || ErrorLevels.HIGH }
        ];
      }
    });
    return errorList;
  }
}
